/**
 * P&L Calculation Hook
 *
 * Calculates profit and loss for trading strategies based on:
 * - Spot orders (buy/sell)
 * - Futures orders (hedge positions)
 * - Funding fees
 * - Current market prices
 *
 * Follows the same logic as Rust evaluate_pnl.rs
 */

import { useMemo } from 'react';
import type {
  ExecutionContextSnapshot,
  RealTimePriceInfo,
  Order,
  FuturesOrder,
  Position,
} from '../types/strategy';

interface PnlCalculationInput {
  ctx: ExecutionContextSnapshot;
  realtimePrices: RealTimePriceInfo;
}

interface SpotPriceInfo {
  /** Average purchase price in original quote currency */
  avgPriceQuote: number | null;
  /** Average purchase price in USDT */
  avgPriceUsdt: number | null;
  /** Current price in original quote currency */
  currentPriceQuote: number | null;
  /** Current price in USDT */
  currentPriceUsdt: number | null;
  /** Quote currency (e.g., 'KRW', 'USDT') */
  quoteCurrency: string | null;
  /** Whether quote currency is non-USDT */
  isNonUsdtQuote: boolean;
  /** Quote to USDT conversion rate */
  quoteToUsdtRate: number;
}

interface FuturesPriceInfo {
  /** Futures entry price (original BEP) */
  avgPrice: number | null;
  /** Current futures price */
  currentPrice: number | null;
}

interface BalanceInfo {
  /** Remaining spot volume */
  spotRemaining: number;
  /** Remaining futures position */
  futuresRemaining: number;
  /** Spot volume sold (Ask orders) */
  spotAsked: number;
  /** Futures volume closed (Bid orders for short position) */
  futuresClosed: number;
  /** Total spot volume purchased */
  spotTotal: number;
  /** Total futures volume shorted */
  futuresTotal: number;
}

interface ProfitInfo {
  /** Spot P&L in USDT */
  spotProfit: number | null;
  /** Spot Realized P&L in USDT */
  spotRealizedPnl: number | null;
  /** Futures P&L in USDT */
  futuresProfit: number | null;
  /** Futures Realized P&L in USDT */
  futuresRealizedPnl: number | null;
  /** Total P&L including funding fees */
  netProfit: number | null;
  /** Total funding fees received/paid */
  fundingFees: number;
}

export interface PnlCalculationResult {
  spot: SpotPriceInfo;
  futures: FuturesPriceInfo;
  balance: BalanceInfo;
  profit: ProfitInfo;
}

/**
 * Helper to safely convert to number (avoid NaN)
 */
function safeNum(v: number | null | undefined): number {
  return v != null && !isNaN(v) ? v : 0;
}

/**
 * Calculate spot-related data from orders
 */
function calculateSpotData(
  spotOrders: Order[],
  realtimePrices: RealTimePriceInfo
): {
  spotPriceInfo: SpotPriceInfo;
  spotSpentUsdt: number;
  spotEarned: number;
  spotRemaining: number;
  spotAsked: number;
  spotTotalVolume: number;
} {
  // Filter buy orders
  const spotBuyOrders = spotOrders.filter(o => o.side === 'Bid' && o.executed_volume > 0);

  let spotSpentUsdt = 0;
  let spotSpentQuote = 0;
  let spotTotalVolume = 0;

  // Get quote currency from first buy order
  const spotQuoteCurrency = spotBuyOrders.length > 0 ? spotBuyOrders[0].quote : null;
  const isNonUsdtQuote = spotQuoteCurrency !== null && spotQuoteCurrency !== 'USDT';
  const spotQuoteToUsdtRate = spotBuyOrders.length > 0
    ? (spotBuyOrders[0].quote_to_usdt_rate ?? 1.0)
    : 1.0;

  // Calculate spent amounts from buy orders
  for (const order of spotBuyOrders) {
    const costInQuote = order.total ?? (order.executed_volume * order.price);
    const feeInQuote = order.paid_fee ?? 0;
    const totalInQuote = costInQuote + feeInQuote;

    spotSpentQuote += totalInQuote;
    const quoteToUsdtRate = order.quote_to_usdt_rate ?? 1.0;
    spotSpentUsdt += totalInQuote * quoteToUsdtRate;
    spotTotalVolume += order.executed_volume;
  }

  // Average prices
  const spotAvgPriceQuote = spotTotalVolume > 0 ? spotSpentQuote / spotTotalVolume : null;
  const spotAvgPriceUsdt = spotTotalVolume > 0 ? spotSpentUsdt / spotTotalVolume : null;

  // Current prices
  const spotCurrentPriceQuote = realtimePrices.spot_best_bid;
  const spotCurrentPriceUsdt = spotCurrentPriceQuote !== null
    ? spotCurrentPriceQuote * spotQuoteToUsdtRate
    : null;

  // Calculate remaining balance
  const spotBidVolume = spotOrders
    .filter(o => o.side === 'Bid')
    .reduce((sum, o) => sum + safeNum(o.executed_volume), 0);
  const spotAskVolume = spotOrders
    .filter(o => o.side === 'Ask')
    .reduce((sum, o) => sum + safeNum(o.executed_volume), 0);

  const spotRemaining = spotBidVolume - spotAskVolume;

  // Calculate earned from sell orders
  const spotEarned = spotOrders
    .filter(o => o.side === 'Ask' && safeNum(o.executed_volume) > 0)
    .reduce((sum, o) => {
      const execVol = safeNum(o.executed_volume);
      const price = safeNum(o.price);
      const total = safeNum(o.total);
      const revenue = total > 0 ? total : (execVol * price);
      const fee = safeNum(o.paid_fee);
      const netRevenue = revenue - fee;
      const rate = safeNum(o.quote_to_usdt_rate) || 1.0;
      return sum + (netRevenue * rate);
    }, 0);

  return {
    spotPriceInfo: {
      avgPriceQuote: spotAvgPriceQuote,
      avgPriceUsdt: spotAvgPriceUsdt,
      currentPriceQuote: spotCurrentPriceQuote,
      currentPriceUsdt: spotCurrentPriceUsdt,
      quoteCurrency: spotQuoteCurrency,
      isNonUsdtQuote,
      quoteToUsdtRate: spotQuoteToUsdtRate,
    },
    spotSpentUsdt,
    spotEarned,
    spotRemaining,
    spotAsked: spotAskVolume,
    spotTotalVolume,
  };
}

/**
 * Calculate futures-related data from orders and position
 */
function calculateFuturesData(
  futuresOrders: FuturesOrder[],
  position: Position | null,
  originalBep: number | null,
  realtimePrices: RealTimePriceInfo
): {
  futuresPriceInfo: FuturesPriceInfo;
  futuresRemaining: number;
  futuresClosed: number;
  futuresRealizedPnl: number;
  futuresUnrealizedPnl: number;
  futuresAskQty: number;
} {
  // Calculate position from orders
  // For short position: Ask opens, Bid closes -> remaining = ask - bid
  // Note: JSON data uses executed_volume, not executed_quantity
  const futuresAskQty = futuresOrders
    .filter(o => o.side === 'Ask')
    .reduce((sum, o) => sum + safeNum(o.executed_volume), 0);
  const futuresBidQty = futuresOrders
    .filter(o => o.side === 'Bid')
    .reduce((sum, o) => sum + safeNum(o.executed_volume), 0);

  // Remaining = opened - closed (Ask opens short, Bid closes short)
  // Use order-based calculation if orders exist, otherwise fallback to position
  const futuresRemaining = futuresOrders.length > 0
    ? futuresAskQty - futuresBidQty
    : Math.abs(safeNum(position?.position_amount));

  // Closed volume = sum of Bid orders (closing short position)
  const closedVolume = futuresBidQty;

  // Current price
  const futuresCurrentPrice = realtimePrices.futures_best_ask || realtimePrices.futures_mark_price;

  // Calculate weighted average close price from Bid orders
  let closedTotal = 0;
  for (const order of futuresOrders) {
    const execQty = safeNum(order.executed_volume);
    if (order.side === 'Bid' && execQty > 0) {
      const avgPrice = safeNum(order.avg_price) || safeNum(order.price);
      closedTotal += execQty * avgPrice;
    }
  }
  const avgClosePrice = closedVolume > 0 ? closedTotal / closedVolume : 0;

  // Realized futures P&L = (entry - close) * closed_volume
  const futuresRealizedPnl = originalBep !== null && closedVolume > 0
    ? (originalBep - avgClosePrice) * closedVolume
    : 0;

  // Unrealized futures P&L = (entry - current) * remaining_volume
  const futuresUnrealizedPnl = originalBep !== null && futuresRemaining > 0 && futuresCurrentPrice !== null
    ? (originalBep - futuresCurrentPrice) * futuresRemaining
    : 0;

  return {
    futuresPriceInfo: {
      avgPrice: originalBep || position?.entry_price || null,
      currentPrice: futuresCurrentPrice,
    },
    futuresRemaining,
    futuresClosed: closedVolume,
    futuresRealizedPnl,
    futuresUnrealizedPnl,
    futuresAskQty,
  };
}

/**
 * Hook to calculate P&L for a trading strategy
 *
 * @param ctx - Execution context snapshot containing orders, positions, etc.
 * @param realtimePrices - Current market prices from WebSocket
 * @returns Calculated P&L data
 */
export function usePnlCalculation({ ctx, realtimePrices }: PnlCalculationInput): PnlCalculationResult {
  return useMemo(() => {
    const spotOrders = Object.values(ctx.orders);
    const futuresOrders = Object.values(ctx.futures_orders);
    const metrics = ctx.metrics;
    const position = ctx.position;
    const originalBep = ctx.original_bep;

    // Calculate spot data
    const {
      spotPriceInfo,
      spotSpentUsdt,
      spotEarned,
      spotRemaining,
      spotAsked,
      spotTotalVolume,
    } = calculateSpotData(spotOrders, realtimePrices);

    // Calculate futures data
    const {
      futuresPriceInfo,
      futuresRemaining,
      futuresClosed,
      futuresRealizedPnl,
      futuresUnrealizedPnl,
      futuresAskQty,
    } = calculateFuturesData(futuresOrders, position, originalBep, realtimePrices);

    // === SPOT P&L ===
    // Spot unrealized value = remaining volume * current price (in USDT)
    const spotUnrealizedValue = spotRemaining > 0 && spotPriceInfo.currentPriceUsdt !== null
      ? spotRemaining * spotPriceInfo.currentPriceUsdt
      : 0;

    // Spot P&L = (earned + unrealized) - spent
    const spotProfit = spotOrders.length > 0
      ? safeNum(spotEarned + spotUnrealizedValue - spotSpentUsdt)
      : null;

    // Spot Realized P&L = Earned (Revenue - Fees) - Cost Basis of Sold Volume
    const spotRealizedPnl = spotAsked > 0 && spotPriceInfo.avgPriceUsdt !== null
      ? safeNum(spotEarned - (spotPriceInfo.avgPriceUsdt * spotAsked))
      : 0;

    // === FUTURES P&L ===
    // Futures P&L = realized + unrealized
    const futuresProfit = originalBep !== null
      ? safeNum(futuresRealizedPnl + futuresUnrealizedPnl)
      : null;

    // === TOTAL P&L ===
    const fundingFees = safeNum(metrics.total_funding_fees);
    const hasAnyProfit = spotProfit !== null || futuresProfit !== null;
    const netProfit = hasAnyProfit
      ? safeNum((spotProfit ?? 0) + (futuresProfit ?? 0) + fundingFees)
      : null;

    return {
      spot: spotPriceInfo,
      futures: futuresPriceInfo,
      balance: {
        spotRemaining,
        futuresRemaining,
        spotAsked,
        futuresClosed,
        spotTotal: spotTotalVolume,
        futuresTotal: futuresAskQty,
      },
      profit: {
        spotProfit,
        spotRealizedPnl,
        futuresProfit,
        futuresRealizedPnl,
        netProfit,
        fundingFees,
      },
    };
  }, [ctx, realtimePrices]);
}

export default usePnlCalculation;
